import { LoaderCircle, Search } from 'lucide-react';
import { useRouter } from 'next/router';
import { useTranslations } from 'next-intl';
import { useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { searchBrands } from '@/api';
import SearchField from '@/components/search-field';
import { getAllBrandsAndTopupsFromResults } from '@/utils';

const spin = keyframes`
  to {
    transform: rotate(360deg);
  }
`;

/*
 * The large rounded field. SearchField brings its own input, icon and
 * suggestion list; the nested rules below resize them for the hero.
 */
const Field = styled.div`
  align-items: center;
  background-color: var(--card);
  border: 1px solid var(--input);
  border-radius: 9999px;
  box-shadow: var(--shadow-md);
  display: flex;
  height: 56px;
  max-width: 560px;
  padding-right: 6px;
  position: relative;
  transition:
    border-color 150ms,
    box-shadow 150ms;
  width: 100%;

  &:focus-within {
    border-color: var(--ring);
    box-shadow:
      var(--shadow-lg),
      0 0 0 3px color-mix(in oklab, var(--ring) 30%, transparent);
  }

  /* Positioned against the whole field, so the list spans its full width. */
  .search-field {
    height: 100%;
    min-width: 0;
    position: static;
    width: auto;
  }

  .search-field > svg {
    left: 20px;
    transform: translateY(-50%);
  }

  .search-field input {
    color: var(--foreground);
    font-size: 16px;
    margin: 0;
    min-width: 0;
    padding: 0 12px 0 54px;
    width: 100%;
  }

  .search-field input::-webkit-search-cancel-button {
    cursor: pointer;
  }

  .search-field ul {
    background-color: var(--popover);
    border: 1px solid var(--border);
    border-radius: var(--radius-xl);
    box-shadow: var(--shadow-lg);
    color: var(--popover-foreground);
    font-size: 16px;
    left: 0;
    max-height: 320px;
    top: calc(100% + 8px);
    z-index: 10;
  }

  .search-field [role='option'] {
    padding: 12px 16px;
  }
`;

const Submit = styled.button`
  align-items: center;
  appearance: none;
  border: none;
  border-radius: 9999px;
  color: var(--primary-foreground);
  cursor: pointer;
  display: inline-flex;
  flex-shrink: 0;
  height: 44px;
  justify-content: center;
  padding: 0;
  transition: background-color 150ms;
  width: 44px;

  /* Doubled class: see the note on fills in components/ui/button.js. */
  && {
    background-color: var(--primary);
  }

  &:hover {
    background-color: color-mix(in oklab, var(--primary) 80%, transparent);
  }

  &:focus-visible {
    box-shadow: 0 0 0 3px color-mix(in oklab, var(--ring) 50%, transparent);
    outline: none;
  }
`;

const Spinner = styled(LoaderCircle)`
  animation: ${spin} 800ms linear infinite;
`;

/**
 * Brand search for the home hero: live suggestions while typing, Enter or the
 * button goes to the catalogue filtered by the query.
 */
const HeroSearch = ({ className }) => {
  const router = useRouter();
  const t = useTranslations('home');
  const [searchTerm, setSearchTerm] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingSuggestions, setIsLoadingSuggestions] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [cachedSuggestions, setCachedSuggestions] = useState([]);

  const search = () => {
    const trimmedQuery = searchTerm.trim();
    setIsLoading(true);

    if (trimmedQuery !== '' && trimmedQuery.length > 2) {
      router.push(`/buy?q=${encodeURIComponent(trimmedQuery)}`);
    } else {
      router.push('/buy');
    }
  };

  const handleChange = async (event) => {
    const { value } = event.target;
    setSuggestions([]);

    if (value.trim()) {
      setIsLoadingSuggestions(true);
      const { results } = await searchBrands({ $search: value });
      const brands = getAllBrandsAndTopupsFromResults(results);

      setSuggestions(brands.sort((brand) => !brand.name.startsWith(value)));
      setIsLoadingSuggestions(false);
    }
  };

  const handleFocus = () => {
    if (cachedSuggestions.length > 0) {
      setSuggestions(cachedSuggestions);
    }
  };

  const handleBlur = () => {
    setCachedSuggestions(suggestions);
    // Leave time for a click on a suggestion to register.
    setTimeout(() => setSuggestions([]), 250);
  };

  return (
    <Field className={className} role="search">
      <SearchField
        isLoadingSuggestions={isLoadingSuggestions}
        onBlur={handleBlur}
        onChange={handleChange}
        onEnter={search}
        onFocus={handleFocus}
        onSuggestionClick={() => setIsLoading(true)}
        search={() => {}}
        searchTerm={searchTerm}
        setSearchTerm={(event) => setSearchTerm(event.target.value)}
        suggestions={suggestions}
      />
      <Submit aria-label={t('search')} onClick={search} type="button">
        {isLoading ? (
          <Spinner aria-hidden="true" size={20} strokeWidth={1.75} />
        ) : (
          <Search aria-hidden="true" size={20} strokeWidth={1.75} />
        )}
      </Submit>
    </Field>
  );
};

export default HeroSearch;
